import React, { useState, useEffect } from "react";
import { TextField, MenuItem, Chip, Divider, Typography, CircularProgress, } from "@material-ui/core";
import { useIsMounted } from "../../hooks/useIsMounted";
const ID_FIELD_NAME = "id";
const LABEL_FIELD_NAME = "name";
const IS_DIVIDER_FIELD_NAME = "isDivider";
const IS_GROUP_FIELD_NAME = "isGroup";
const DISABLED_FIELD_NAME = "disabled";
const IS_ACTIVE_FIELD_NAME = "active";
export const SelectListField = ({ service, name, label, value, error, helperText, isAutofocusField, disabled, onChange, onBlur, multiple, dataTest, }) => {
    let isMountedRef = useIsMounted();
    const [items, setItems] = useState([]);
    const [internalValue, setInternalValue] = useState(multiple ? [] : "");
    const [isFocussed, setIsFocussed] = useState(false);
    const [loadingState, setLoadingState] = useState("NOT_LOADED");
    const shouldLoad = (isFocussed && !value) ||
        (Array.isArray(value) && !value.length) || // load when the field is entered and is blank
        ((value || value === false) && loadingState === "NOT_LOADED"); // or if the page loads with a value for the field
    useEffect(() => {
        if (!shouldLoad) {
            return;
        }
        setLoadingState("LOADING");
        service
            .getAll()
            .then(({ data }) => {
            if (!isMountedRef.current) {
                return;
            }
            // check to see if our value(s) is/are present in the lookup items
            const valuesToCheck = multiple ? value : [value];
            for (const valueToCheck of valuesToCheck) {
                if (valueToCheck && !data.some(item => item[ID_FIELD_NAME] === valueToCheck)) {
                    console.error(`${valueToCheck} is not present as an item id in the loaded list of items in SelectListField: ${name}`);
                    data = [...data, { [ID_FIELD_NAME]: valueToCheck, [LABEL_FIELD_NAME]: valueToCheck }];
                }
            }
            setItems(data);
            setLoadingState(data.length ? "LOADED" : "LOADED_EMPTY");
            setInternalValue(value);
        })
            .catch(e => {
            console.log(e);
            if (!isMountedRef.current) {
                return;
            }
            setLoadingState("LOADED_ERROR");
        });
    }, [service, shouldLoad, isMountedRef, value, name, multiple]);
    const shouldReset = loadingState !== "NOT_LOADED" && !value && internalValue;
    useEffect(() => {
        // if the field is reset by logic on the page e.g. a parent dependent dropdown
        if (isMountedRef.current && shouldReset) {
            setLoadingState("NOT_LOADED");
            setItems([]);
            setInternalValue("");
        }
    }, [isMountedRef, shouldReset]);
    return (React.createElement(TextField, { variant: "outlined", autoFocus: isAutofocusField, disabled: disabled, value: internalValue, name: name, label: label, select: true, SelectProps: multiple
            ? {
                multiple: true,
                renderValue: getMultipleModeRender(items),
            }
            : undefined, fullWidth: true, error: error, helperText: helperText, onChange: e => {
            setInternalValue(e.target.value);
            onChange(e);
        }, onFocus: () => {
            setIsFocussed(true);
        }, onBlur: e => {
            setIsFocussed(false);
            onBlur(e);
        }, "data-test": dataTest },
        loadingState === "LOADING" && getIsLoadingContent(),
        loadingState === "LOADED_ERROR" && getErrorContent(),
        loadingState === "LOADED_EMPTY" && getEmptyContent(),
        loadingState === "LOADED" && getLoadedContent(items, value)));
};
const getIsLoadingContent = () => {
    return (React.createElement(MenuItem, { key: "PLEASE_WAIT", disabled: true },
        React.createElement(Typography, { variant: "body1", color: "inherit", style: { flex: 1 } }, "Loading, please wait..."),
        React.createElement(CircularProgress, { size: 24, color: "secondary" })));
};
const getErrorContent = () => {
    return (React.createElement(MenuItem, { key: "ERROR", disabled: true },
        React.createElement(Typography, { variant: "body1", color: "inherit", style: { flex: 1 } }, "An Error has occurred loading the data")));
};
const getEmptyContent = () => {
    return (React.createElement(MenuItem, { key: "ERROR", disabled: true },
        React.createElement(Typography, { variant: "body1", color: "inherit", style: { flex: 1 } }, "No records are available")));
};
const getLoadedContent = (items, value) => {
    return items.map((item, index) => {
        if (item[IS_DIVIDER_FIELD_NAME]) {
            return React.createElement(Divider, { key: "divider-" + index });
        }
        else if (item[IS_GROUP_FIELD_NAME]) {
            return (React.createElement(MenuItem, { key: "group-header-" + index, disabled: true }, item[IS_GROUP_FIELD_NAME]));
        }
        else {
            const isVisibleValue = item[LABEL_FIELD_NAME] && String(item[LABEL_FIELD_NAME]).trim().length;
            const label = isVisibleValue ? item[LABEL_FIELD_NAME] : React.createElement(React.Fragment, null, "\u00A0");
            const isInactiveValueSelected = item.id === value;
            return ((item[IS_ACTIVE_FIELD_NAME] !== false || isInactiveValueSelected) && (React.createElement(MenuItem, { className: "IndentMenuItem", key: item[ID_FIELD_NAME], value: item[ID_FIELD_NAME], disabled: item[DISABLED_FIELD_NAME] || isInactiveValueSelected }, label)));
        }
    });
};
const getMultipleModeRender = (items) => (selected) => (React.createElement("div", { style: { display: "flex", flexWrap: "wrap" } }, selected.map(value => {
    var _a;
    return (React.createElement(Chip, { color: "primary", key: value, label: ((_a = items.find(x => x.id === value)) === null || _a === void 0 ? void 0 : _a.name) || value, className: "chip" }));
})));
